import * as React from 'react';
import { useEffect } from 'react';
import { FluentProvider, webLightTheme, Tab, TabList, tokens } from '@fluentui/react-components';
import { ImageMultipleRegular } from '@fluentui/react-icons';
import ElementsPanel from './ElementsPanel';
import GraphToolbar from './GraphToolbar';
import ErrorBoundary from './components/shared/ErrorBoundary';
import Toasts from './components/shared/Toast';
import TemplatesPanel from './components/templates/TemplatesPanel';
import { BgImagesPanel } from './components/BgImagesPanel';
import useUiStore from './store/uiStore';
import type { ActiveTab } from './store/uiStore';
import { useActiveTemplate } from './hooks/useActiveTemplate';
import { useAutoRenumber } from './hooks/useAutoRenumber';

const App: React.FC = () => {
  const activeTab = useUiStore((s) => s.activeTab) as ActiveTab;
  const setActiveTab = useUiStore((s) => s.setActiveTab);
  const { syncFromDocument, profile } = useActiveTemplate();

  // Auto-renumber exhibits whenever the user deletes one (Backspace / Delete).
  useAutoRenumber(profile);

  const showBgImagesTab = profile?.features?.heroBgImages === true;

  // If the active tab is bg-images but the current template no longer
  // supports it (e.g. user switched templates), fall back to tools.
  const resolvedTab: ActiveTab =
    activeTab === 'bg-images' && !showBgImagesTab ? 'tools' : activeTab;

  useEffect(() => {
    void syncFromDocument();
  }, [syncFromDocument]);

  return (
    <FluentProvider theme={webLightTheme}>
      <ErrorBoundary fallback={<div style={{ padding: 12 }}>Something went wrong.</div>}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
          <header style={{ padding: 12, background: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundInverted }}>
            <div style={{ fontWeight: 600 }}>SBIMF-Template-Plugin</div>
          </header>
          
          <div style={{ padding: 8 }}>
            <TabList
              selectedValue={resolvedTab}
              onTabSelect={(_, d) => setActiveTab(d.value as ActiveTab)}
            >
              <Tab value="tools">Tools</Tab>
              <Tab value="templates">Templates</Tab>
              {showBgImagesTab && (
                <Tab
                  value="bg-images"
                  icon={<ImageMultipleRegular aria-hidden="true" />}
                  aria-label="Background images"
                >
                  BG Images
                </Tab>
              )}
            </TabList>
          </div>

          <main style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
            {resolvedTab === 'tools' && (
              <>
                <GraphToolbar />
                <ElementsPanel />
              </>
            )}
            {resolvedTab === 'templates' && <TemplatesPanel />}
            {resolvedTab === 'bg-images' && showBgImagesTab && <BgImagesPanel />}
          </main>

          <Toasts />
        </div>
      </ErrorBoundary>
    </FluentProvider>
  );
};

export default App;
